import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Brain, AlertCircle, History, ArrowLeft, ArrowRight, Sparkles, Calendar } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { ResultsGraphTR } from '../../components/ResultsGraphTR';
import { HarmonicGraphTR } from '../../components/HarmonicGraphTR';
import { AIAnalysisPanel } from '../../components/ai/AIAnalysisPanel';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { fetchMyPathways } from '../../lib/pathways';
import type { AssessmentResult } from '../../types/results';
import type { AssignedPathway } from '../../types/pathway';

interface UserProfile {
  full_name: string | null;
  email: string | null;
}

function MyPathwaysTR({ pathways }: { pathways: AssignedPathway[] }) {
  if (pathways.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7 }}
      className="mb-8"
    >
      <div className="freud-card-elevated p-8">
        <h2 className="freud-heading-xl text-chocolate-900 mb-6">Gelişim Yolculuklarım</h2>
        <div className="grid gap-4">
          {pathways.map((pw) => {
            const progress = pw.totalStages > 0
              ? Math.round((pw.completedStages / pw.totalStages) * 100)
              : 0;
            return (
              <Link
                key={pw.assignmentId}
                to={`/tr/dashboard/pathway/${pw.assignmentId}`}
                className="block p-5 bg-cream-50 hover:bg-cream-100 rounded-2xl transition-colors border border-cream-200"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-chocolate-800">{pw.pathway.name}</h3>
                  <span className="text-sm font-medium text-chocolate-600">{progress}%</span>
                </div>
                <div className="h-2 bg-cream-200 rounded-full overflow-hidden mb-2">
                  <div className="h-full bg-orange-50 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-xs text-chocolate-500">
                  {pw.completedStages} / {pw.totalStages} aşama tamamlandı
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

export function RespondentDashboardTR() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [results, setResults] = useState<AssessmentResult[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canRetake, setCanRetake] = useState(false);
  const [nextRetakeDate, setNextRetakeDate] = useState<Date | null>(null);
  const [hasAiAccess, setHasAiAccess] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [myPathways, setMyPathways] = useState<AssignedPathway[]>([]);

  const loadResults = useCallback(async (userId: string) => {
    try {
      const { data: resultsData, error: resultsError } = await supabase
        .from('results')
        .select('*')
        .eq('user_id', userId)
        .order('completed_at', { ascending: false });

      if (resultsError) throw resultsError;

      if (resultsData && resultsData.length > 0) {
        const allResults: AssessmentResult[] = resultsData.map(doc => ({
          id: doc.id,
          userId: doc.user_id,
          coachId: doc.coach_id || undefined,
          partnerId: doc.partner_id || undefined,
          scores: (doc.scores || []) as any,
          harmonicScores: (doc.harmonic_scores || []) as any,
          completedAt: new Date(doc.completed_at || doc.created_at || Date.now()),
          aiAnalysis: doc.coach_ai_analysis || doc.ai_analysis || undefined,
          coachAiAnalysis: doc.coach_ai_analysis || undefined,
          version: doc.version || 1,
          originalResultId: doc.original_result_id || undefined,
        }));

        setResults(allResults);

        const { data: respondentData } = await supabase
          .from('respondents')
          .select('next_retake_date, is_active, coach_id, ai_analysis_visible, id')
          .eq('user_id', userId)
          .maybeSingle();

        if (respondentData) {
          const nextRetake = respondentData.next_retake_date ? new Date(respondentData.next_retake_date) : null;
          const retakeEnabled = respondentData.is_active !== false;
          setCanRetake(retakeEnabled && (!nextRetake || nextRetake <= new Date()));
          setNextRetakeDate(nextRetake);

          if (respondentData.coach_id) {
            const { data: coachData } = await supabase
              .from('coaches')
              .select('tier')
              .eq('id', respondentData.coach_id)
              .maybeSingle();

            if (coachData) {
              const coachTierAllows = coachData.tier === 'advanced' || coachData.tier === 'partner';
              const visibleToRespondent = respondentData.ai_analysis_visible === true;
              setHasAiAccess(coachTierAllows && visibleToRespondent);
            }
          }

          const pathways = await fetchMyPathways(respondentData.id);
          setMyPathways(pathways);
        }
      }
    } catch (err: any) {
      console.error('Error loading results:', err);
      setError(err.message || 'Sonuçlar yüklenemedi');
    }
  }, []);

  useEffect(() => {
    if (!user) { navigate('/tr/login'); return; }
    const loadData = async () => {
      try {
        const { data: profile } = await supabase
          .from('users')
          .select('full_name, email')
          .eq('id', user.id)
          .maybeSingle();
        setUserProfile(profile || { full_name: null, email: user.email ?? null });
        await loadResults(user.id);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [user, navigate, loadResults]);

  const handlePreviousResult = () => { if (currentPage < results.length) setCurrentPage(prev => prev + 1); };
  const handleNextResult = () => { if (currentPage > 1) setCurrentPage(prev => prev - 1); };

  const handleAnalysisUpdate = (analysis: string) => {
    setResults(prev => prev.map((r, i) => i === currentPage - 1 ? { ...r, aiAnalysis: analysis } : r));
  };

  if (isLoading) return <LoadingSpinner message="Sonuçlarınız yükleniyor..." />;

  if (error) {
    return (
      <div className="p-6 md:p-8">
        <div className="freud-card-elevated p-8 text-center">
          <div className="flex items-center gap-2 bg-warm-100 text-warm-700 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" /><p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="p-6 md:p-8">
        <div className="freud-card-elevated p-10 text-center">
          <div className="w-16 h-16 bg-sage-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Brain className="w-8 h-8 text-sage-600" />
          </div>
          <h2 className="freud-heading-xl text-chocolate-900 mb-4">Değerlendirme Sonucu Bulunamadı</h2>
          <p className="freud-text-base text-chocolate-500 mb-8 max-w-md mx-auto">Henüz bir değerlendirme tamamlamadınız. Kişiselleştirilmiş içgörüler almak için ilk değerlendirmenizi yapın.</p>
          <Link to="/tr/instructions" className="freud-btn-primary inline-flex items-center gap-2">
            Değerlendirmeye Başla
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    );
  }

  const selectedResult = results[currentPage - 1];
  const totalVersions = results.length;

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-sage-500 rounded-2xl flex items-center justify-center shadow-soft">
            <Brain className="w-8 h-8 text-white" />
          </div>
          <div>
            {userProfile && (
              <div className="mb-1">
                {userProfile.full_name && <p className="text-sm font-semibold text-chocolate-800">{userProfile.full_name}</p>}
                <p className="text-xs text-chocolate-500">{userProfile.email}</p>
              </div>
            )}
            <h1 className="freud-heading-2xl text-chocolate-900">Değerlendirme Raporunuz</h1>
            <div className="flex items-center gap-2 text-chocolate-500 mt-1">
              <Calendar className="w-4 h-4" />
              <span>Versiyon {totalVersions - currentPage + 1}</span>
              <span>- Tamamlanma: {selectedResult.completedAt.toLocaleDateString('tr-TR')}</span>
            </div>
          </div>
        </div>
        {results.length > 1 && (
          <div className="flex items-center gap-4">
            <button onClick={handleNextResult} disabled={currentPage <= 1} className="p-2.5 rounded-xl bg-cream-200 text-chocolate-700 hover:bg-cream-300 transition-all disabled:opacity-50">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="text-chocolate-600 font-medium">{currentPage} / {totalVersions}</div>
            <button onClick={handlePreviousResult} disabled={currentPage >= totalVersions} className="p-2.5 rounded-xl bg-cream-200 text-chocolate-700 hover:bg-cream-300 transition-all disabled:opacity-50">
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-8 mb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="freud-card-elevated p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
              <Brain className="w-5 h-5 text-sage-600" />
            </div>
            <h2 className="freud-heading-xl text-chocolate-900">Karakteristik Profil</h2>
          </div>
          <ResultsGraphTR scores={selectedResult.scores} />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="freud-card-elevated p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-warm-100 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-warm-600" />
            </div>
            <h2 className="freud-heading-xl text-chocolate-900">Harmonik Ölçek Profili</h2>
          </div>
          <HarmonicGraphTR scores={selectedResult.harmonicScores} />
        </motion.div>
      </div>

      {hasAiAccess && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mb-8">
          <AIAnalysisPanel
            locale="tr"
            analysis={selectedResult.aiAnalysis}
            scores={selectedResult.scores}
            harmonicScores={selectedResult.harmonicScores}
            resultId={selectedResult.id}
            onAnalysisUpdate={handleAnalysisUpdate}
          />
        </motion.div>
      )}

      {!hasAiAccess && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mb-8">
          <div className="text-chocolate-500 text-center bg-cream-100 px-6 py-4 rounded-xl">
            Yapay zekâ analizi henüz mevcut değil. Daha fazla bilgi için koçunuzla iletişime geçin.
          </div>
        </motion.div>
      )}

      <MyPathwaysTR pathways={myPathways} />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }} className="flex flex-col items-center gap-4">
        {canRetake && (
          <Link to="/tr/instructions" className="freud-btn-primary inline-flex items-center gap-2">
            Değerlendirmeyi Tekrarla
            <ArrowRight className="w-5 h-5" />
          </Link>
        )}
        {!canRetake && nextRetakeDate && (
          <div className="flex items-center gap-2 px-4 py-3 bg-sand-100 rounded-xl text-sand-800">
            <Calendar className="w-5 h-5" />
            Sonraki tekrar: {nextRetakeDate.toLocaleDateString('tr-TR')}
          </div>
        )}
        {results.length > 1 && (
          <div className="flex items-center gap-2 text-chocolate-500 mt-4">
            <History className="w-4 h-4" />
            <span>Toplam Değerlendirme: {results.length}</span>
          </div>
        )}
      </motion.div>
    </div>
  );
}
